import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';

const Product3DViewer = ({ productName, productDescription, initialColor = "#3498db" }) => {
  const mountRef = useRef(null);
  const controlsRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState('front');

  useEffect(() => {
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8f9fa);

    // Camera
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
    camera.position.z = 5;
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    
    // Clear any existing canvas
    while (mountRef.current.firstChild) {
      mountRef.current.removeChild(mountRef.current.firstChild);
    }
    
    mountRef.current.appendChild(renderer.domElement);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 5, 5);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-5, -5, -5);
    scene.add(fillLight);

    // Add controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.rotateSpeed = 0.5;
    controls.maxPolarAngle = Math.PI / 1.5;
    controls.minDistance = 3;
    controls.maxDistance = 8;
    controlsRef.current = controls;

    // Create product 3D model
    let productMesh;

    // Function to create a product model based on name
    const createProductModel = () => {
      scene.remove(scene.getObjectByName('productModel'));
      
      const color = new THREE.Color(initialColor);
      const productMaterial = new THREE.MeshPhysicalMaterial({ 
        color: color,
        metalness: 0.2,
        roughness: 0.3,
        clearcoat: 0.4,
        clearcoatRoughness: 0.2
      });
      
      // Choose geometry based on product name (simplified example)
      if (productName.toLowerCase().includes('kite')) {
        // Improved phone model
        const group = new THREE.Group();
        group.name = 'productModel';
        
        // Phone body with rounded edges
        const phoneShape = new THREE.Shape();
        const width = 2;
        const height = 3.5;
        const radius = 0.3;

        phoneShape.moveTo(-width/2 + radius, -height/2);
        phoneShape.lineTo(width/2 - radius, -height/2);
        phoneShape.quadraticCurveTo(width/2, -height/2, width/2, -height/2 + radius);
        phoneShape.lineTo(width/2, height/2 - radius);
        phoneShape.quadraticCurveTo(width/2, height/2, width/2 - radius, height/2);
        phoneShape.lineTo(-width/2 + radius, height/2);
        phoneShape.quadraticCurveTo(-width/2, height/2, -width/2, height/2 - radius);
        phoneShape.lineTo(-width/2, -height/2 + radius);
        phoneShape.quadraticCurveTo(-width/2, -height/2, -width/2 + radius, -height/2);

        const extrudeSettings = {
          steps: 2,
          depth: 0.2,
          bevelEnabled: true,
          bevelThickness: 0.05,
          bevelSize: 0.05,
          bevelOffset: 0,
          bevelSegments: 5
        };

        const phoneGeometry = new THREE.ExtrudeGeometry(phoneShape, extrudeSettings);
        const phoneMesh = new THREE.Mesh(phoneGeometry, productMaterial);
        
        // Phone screen with rounded corners
        const screenShape = new THREE.Shape();
        const screenWidth = 1.8;
        const screenHeight = 3.2;
        const screenRadius = 0.2;

        screenShape.moveTo(-screenWidth/2 + screenRadius, -screenHeight/2);
        screenShape.lineTo(screenWidth/2 - screenRadius, -screenHeight/2);
        screenShape.quadraticCurveTo(screenWidth/2, -screenHeight/2, screenWidth/2, -screenHeight/2 + screenRadius);
        screenShape.lineTo(screenWidth/2, screenHeight/2 - screenRadius);
        screenShape.quadraticCurveTo(screenWidth/2, screenHeight/2, screenWidth/2 - screenRadius, screenHeight/2);
        screenShape.lineTo(-screenWidth/2 + screenRadius, screenHeight/2);
        screenShape.quadraticCurveTo(-screenWidth/2, screenHeight/2, -screenWidth/2, screenHeight/2 - screenRadius);
        screenShape.lineTo(-screenWidth/2, -screenHeight/2 + screenRadius);
        screenShape.quadraticCurveTo(-screenWidth/2, -screenHeight/2, -screenWidth/2 + screenRadius, -screenHeight/2);

        const screenExtrudeSettings = {
          steps: 1,
          depth: 0.01,
          bevelEnabled: false
        };

        const screenGeometry = new THREE.ExtrudeGeometry(screenShape, screenExtrudeSettings);
        const screenMaterial = new THREE.MeshPhongMaterial({ 
          color: 0x222222,
          shininess: 100
        });
        const screenMesh = new THREE.Mesh(screenGeometry, screenMaterial);
        screenMesh.position.z = 0.11;
        
        // Camera module
        const cameraGroup = new THREE.Group();
        const cameraBumpGeometry = new THREE.CylinderGeometry(0.15, 0.15, 0.05, 32);
        const cameraBumpMaterial = new THREE.MeshPhongMaterial({ color: 0x333333 });
        const cameraBump = new THREE.Mesh(cameraBumpGeometry, cameraBumpMaterial);
        cameraBump.rotation.x = Math.PI / 2;
        
        // Camera lens
        const lensGeometry = new THREE.CircleGeometry(0.08, 32);
        const lensMaterial = new THREE.MeshPhongMaterial({ 
          color: 0x111111,
          shininess: 120 
        });
        const lensMesh = new THREE.Mesh(lensGeometry, lensMaterial);
        lensMesh.position.z = 0.03;
        
        cameraBump.add(lensMesh);
        cameraGroup.add(cameraBump);
        cameraGroup.position.set(0.5, 1.4, -0.13);
        
        group.add(phoneMesh);
        group.add(screenMesh);
        group.add(cameraGroup);
        
        scene.add(group);
        productMesh = group;
      } 
      else if (productName.toLowerCase().includes('console')) {
        // Improved console/dashboard
        const group = new THREE.Group();
        group.name = 'productModel';
        
        // Monitor with curved edges
        const monitorShape = new THREE.Shape();
        const width = 3;
        const height = 2;
        const radius = 0.2;

        monitorShape.moveTo(-width/2 + radius, -height/2);
        monitorShape.lineTo(width/2 - radius, -height/2);
        monitorShape.quadraticCurveTo(width/2, -height/2, width/2, -height/2 + radius);
        monitorShape.lineTo(width/2, height/2 - radius);
        monitorShape.quadraticCurveTo(width/2, height/2, width/2 - radius, height/2);
        monitorShape.lineTo(-width/2 + radius, height/2);
        monitorShape.quadraticCurveTo(-width/2, height/2, -width/2, height/2 - radius);
        monitorShape.lineTo(-width/2, -height/2 + radius);
        monitorShape.quadraticCurveTo(-width/2, -height/2, -width/2 + radius, -height/2);

        const extrudeSettings = {
          steps: 1,
          depth: 0.2,
          bevelEnabled: true,
          bevelThickness: 0.05,
          bevelSize: 0.02,
          bevelSegments: 3
        };

        const monitorGeometry = new THREE.ExtrudeGeometry(monitorShape, extrudeSettings);
        const monitorMesh = new THREE.Mesh(monitorGeometry, productMaterial);
        
        // Screen
        const screenShape = new THREE.Shape();
        const screenWidth = 2.8;
        const screenHeight = 1.8;
        const screenRadius = 0.1;

        screenShape.moveTo(-screenWidth/2 + screenRadius, -screenHeight/2);
        screenShape.lineTo(screenWidth/2 - screenRadius, -screenHeight/2);
        screenShape.quadraticCurveTo(screenWidth/2, -screenHeight/2, screenWidth/2, -screenHeight/2 + screenRadius);
        screenShape.lineTo(screenWidth/2, screenHeight/2 - screenRadius);
        screenShape.quadraticCurveTo(screenWidth/2, screenHeight/2, screenWidth/2 - screenRadius, screenHeight/2);
        screenShape.lineTo(-screenWidth/2 + screenRadius, screenHeight/2);
        screenShape.quadraticCurveTo(-screenWidth/2, screenHeight/2, -screenWidth/2, screenHeight/2 - screenRadius);
        screenShape.lineTo(-screenWidth/2, -screenHeight/2 + screenRadius);
        screenShape.quadraticCurveTo(-screenWidth/2, -screenHeight/2, -screenWidth/2 + screenRadius, -screenHeight/2);

        const screenExtrudeSettings = {
          steps: 1,
          depth: 0.01,
          bevelEnabled: false
        };

        const screenGeometry = new THREE.ExtrudeGeometry(screenShape, screenExtrudeSettings);
        const screenMaterial = new THREE.MeshPhongMaterial({ 
          color: 0x222222, 
          shininess: 100 
        });
        const screenMesh = new THREE.Mesh(screenGeometry, screenMaterial);
        screenMesh.position.z = 0.15;
        
        // Improved stand with curve
        const standCurve = new THREE.CubicBezierCurve3(
          new THREE.Vector3(0, -1, 0),
          new THREE.Vector3(0, -1.2, -0.5),
          new THREE.Vector3(0, -1.8, -0.3),
          new THREE.Vector3(0, -2, 0)
        );
        
        const standPoints = standCurve.getPoints(20);
        const standGeometry = new THREE.LatheGeometry(standPoints, 20, 0, Math.PI * 2);
        const standMaterial = new THREE.MeshPhongMaterial({ color: 0x666666 });
        const standMesh = new THREE.Mesh(standGeometry, standMaterial);
        standMesh.scale.set(0.2, 1, 0.2);
        
        // Base
        const baseGeometry = new THREE.CylinderGeometry(0.8, 0.8, 0.1, 32);
        const baseMesh = new THREE.Mesh(baseGeometry, standMaterial);
        baseMesh.position.y = -2;
        
        group.add(monitorMesh);
        group.add(screenMesh);
        group.add(standMesh);
        group.add(baseMesh);
        
        scene.add(group);
        productMesh = group;
      }
      else if (productName.toLowerCase().includes('coin')) {
        // Enhanced coin investment app visualization
        const group = new THREE.Group();
        group.name = 'productModel';
        
        // Main coin with more detail
        const coinGeometry = new THREE.CylinderGeometry(1.5, 1.5, 0.15, 64);
        const coinEdgeGeometry = new THREE.TorusGeometry(1.5, 0.075, 16, 64);
        
        const coinMaterial = new THREE.MeshStandardMaterial({ 
          color: 0xFFD700,
          metalness: 0.9,
          roughness: 0.1
        });
        
        const coinMesh = new THREE.Mesh(coinGeometry, coinMaterial);
        coinMesh.rotation.x = Math.PI / 2;
        
        const coinEdge = new THREE.Mesh(coinEdgeGeometry, coinMaterial);
        coinEdge.rotation.x = Math.PI / 2;
        
        group.add(coinMesh);
        group.add(coinEdge);
        
        // Create coin details/patterns
        const detailGeometry = new THREE.RingGeometry(1, 1.3, 64);
        const detailMaterial = new THREE.MeshStandardMaterial({
          color: 0xFFD700,
          metalness: 0.7,
          roughness: 0.2
        });
        const detailMesh = new THREE.Mesh(detailGeometry, detailMaterial);
        detailMesh.rotation.x = Math.PI / 2;
        detailMesh.position.z = 0.08;
        
        group.add(detailMesh);
        
        // Add second smaller coin slightly behind
        const smallCoinGeometry = new THREE.CylinderGeometry(1.2, 1.2, 0.15, 64);
        const smallCoinMesh = new THREE.Mesh(smallCoinGeometry, coinMaterial);
        smallCoinMesh.rotation.x = Math.PI / 2;
        smallCoinMesh.position.set(-0.6, -0.6, -0.3);
        
        group.add(smallCoinMesh);
        
        // Use simple circle for rupee symbol instead of text geometry
        const symbolGeometry = new THREE.CircleGeometry(0.6, 32);
        const symbolMaterial = new THREE.MeshStandardMaterial({ 
          color: 0xD4AF37,
          metalness: 1.0,
          roughness: 0.1
        });
        const symbolMesh = new THREE.Mesh(symbolGeometry, symbolMaterial);
        symbolMesh.position.z = 0.08;
        
        group.add(symbolMesh);
        
        scene.add(group);
        productMesh = group;
      }
      else if (productName.toLowerCase().includes('api')) {
        // Enhanced API Connection visualization
        const group = new THREE.Group();
        group.name = 'productModel';
        
        // Materials
        const nodeMaterial = new THREE.MeshPhysicalMaterial({ 
          color: initialColor,
          metalness: 0.2,
          roughness: 0.5,
          clearcoat: 0.3
        });
        
        const connectionMaterial = new THREE.MeshPhongMaterial({ 
          color: 0x888888,
          transparent: true,
          opacity: 0.7
        });
        
        // Central node with glow effect
        const centralNodeGeometry = new THREE.SphereGeometry(0.7, 32, 32);
        const centralNode = new THREE.Mesh(centralNodeGeometry, nodeMaterial);
        
        // Add outer glow sphere
        const glowGeometry = new THREE.SphereGeometry(0.8, 32, 32);
        const glowMaterial = new THREE.MeshBasicMaterial({
          color: initialColor,
          transparent: true,
          opacity: 0.2
        });
        const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
        centralNode.add(glowMesh);
        
        // Satellite nodes with dynamic positioning
        const nodeCount = 6;
        const radius = 2;
        const nodes = [];
        const connections = [];
        
        // Create nodes in a more 3D arrangement
        for (let i = 0; i < nodeCount; i++) {
          // Calculate positions on a semi-sphere
          const theta = (i / nodeCount) * Math.PI * 2;
          const phi = Math.random() * Math.PI / 2;
          
          const x = radius * Math.sin(phi) * Math.cos(theta);
          const y = radius * Math.sin(phi) * Math.sin(theta);
          const z = radius * Math.cos(phi);
          
          const node = new THREE.Mesh(
            new THREE.SphereGeometry(0.3, 16, 16),
            nodeMaterial
          );
          node.position.set(x, y, z);
          
          // Create data transmission animation rings
          const ringGeometry = new THREE.TorusGeometry(0.1, 0.02, 8, 32);
          const ringMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.8
          });
          const ring = new THREE.Mesh(ringGeometry, ringMaterial);
          
          // Orient the ring to face the central node
          ring.lookAt(new THREE.Vector3(0, 0, 0));
          
          // Position ring partway between node and center
          ring.position.copy(node.position.clone().multiplyScalar(0.4));
          
          // Create connection cylinder with curve
          const curvePoints = [];
          const start = new THREE.Vector3(0, 0, 0);  // Central node
          const end = new THREE.Vector3(x, y, z);    // Satellite node
          const mid = new THREE.Vector3(
            x * 0.5 + (Math.random() - 0.5) * 0.3,
            y * 0.5 + (Math.random() - 0.5) * 0.3,
            z * 0.5 + (Math.random() - 0.5) * 0.3
          );
          
          // Create a quadratic bezier curve
          const curve = new THREE.QuadraticBezierCurve3(start, mid, end);
          const points = curve.getPoints(20);
          
          // Create tube geometry along the curve
          const tubeGeometry = new THREE.TubeGeometry(curve, 20, 0.05, 8, false);
          const connection = new THREE.Mesh(tubeGeometry, connectionMaterial);
          
          nodes.push(node);
          connections.push(connection);
          group.add(ring);
        }
        
        group.add(centralNode);
        nodes.forEach(node => group.add(node));
        connections.forEach(connection => group.add(connection));
        
        scene.add(group);
        productMesh = group;
      }
      else {
        // Enhanced default (Varsity or other products) - book/documentation
        const group = new THREE.Group();
        group.name = 'productModel';
        
        // Book cover with curved spine
        const coverWidth = 3;
        const coverHeight = 4;
        const coverDepth = 0.3;
        const spineRadius = 0.3;
        
        // Create book spine with bend
        const spineShape = new THREE.Shape();
        spineShape.moveTo(-coverWidth/2, -coverHeight/2);
        spineShape.lineTo(coverWidth/2, -coverHeight/2);
        spineShape.lineTo(coverWidth/2, coverHeight/2);
        spineShape.lineTo(-coverWidth/2, coverHeight/2);
        spineShape.lineTo(-coverWidth/2, -coverHeight/2);
        
        // Add cutout for pages
        const hole = new THREE.Path();
        hole.moveTo(-coverWidth/2 + coverDepth, -coverHeight/2 + coverDepth);
        hole.lineTo(coverWidth/2 - coverDepth, -coverHeight/2 + coverDepth);
        hole.lineTo(coverWidth/2 - coverDepth, coverHeight/2 - coverDepth);
        hole.lineTo(-coverWidth/2 + coverDepth, coverHeight/2 - coverDepth);
        hole.lineTo(-coverWidth/2 + coverDepth, -coverHeight/2 + coverDepth);
        spineShape.holes.push(hole);
        
        const extrudeSettings = {
          steps: 2,
          depth: coverDepth,
          bevelEnabled: true,
          bevelThickness: 0.05,
          bevelSize: 0.05,
          bevelSegments: 3
        };

        const bookGeometry = new THREE.ExtrudeGeometry(spineShape, extrudeSettings);
        const bookMesh = new THREE.Mesh(bookGeometry, productMaterial);
        
        // Pages with slight curve
        const pagesGroup = new THREE.Group();
        const pageCount = 10;
        const pageDepth = 0.2 / pageCount;
        
        for (let i = 0; i < pageCount; i++) {
          const pageShape = new THREE.Shape();
          pageShape.moveTo(-coverWidth/2 + coverDepth * 1.2, -coverHeight/2 + coverDepth * 1.2);
          pageShape.lineTo(coverWidth/2 - coverDepth * 1.2, -coverHeight/2 + coverDepth * 1.2);
          pageShape.lineTo(coverWidth/2 - coverDepth * 1.2, coverHeight/2 - coverDepth * 1.2);
          pageShape.lineTo(-coverWidth/2 + coverDepth * 1.2, coverHeight/2 - coverDepth * 1.2);
          pageShape.lineTo(-coverWidth/2 + coverDepth * 1.2, -coverHeight/2 + coverDepth * 1.2);
          
          const pageExtrudeSettings = {
            steps: 1,
            depth: pageDepth,
            bevelEnabled: false
          };
          
          const pageGeometry = new THREE.ExtrudeGeometry(pageShape, pageExtrudeSettings);
          const pageMaterial = new THREE.MeshStandardMaterial({
            color: 0xfafafa,
            roughness: 0.8,
            side: THREE.DoubleSide
          });
          
          const pageMesh = new THREE.Mesh(pageGeometry, pageMaterial);
          pageMesh.position.z = i * pageDepth;
          
          // Add slight curve to pages
          pageMesh.rotation.y = (i / pageCount) * 0.05;
          
          pagesGroup.add(pageMesh);
        }
        
        pagesGroup.position.z = 0.1;
        
        // Create simple bookmark
        const bookmarkGeometry = new THREE.BoxGeometry(0.8, 0.5, 0.02);
        const bookmarkMaterial = new THREE.MeshStandardMaterial({
          color: 0xE74C3C,
          roughness: 0.7
        });
        const bookmark = new THREE.Mesh(bookmarkGeometry, bookmarkMaterial);
        bookmark.position.set(0.5, coverHeight/2 + 0.1, 0.15);
        bookmark.rotation.z = Math.PI * 0.1;
        
        group.add(bookMesh);
        group.add(pagesGroup);
        group.add(bookmark);
        
        scene.add(group);
        productMesh = group;
      }
      
      // Position the model
      if (productMesh) {
        productMesh.position.set(0, 0, 0);
        productMesh.castShadow = true;
        productMesh.receiveShadow = true;
      }
      
      setIsLoading(false);
    };
    
    createProductModel();
    
    // Set up predefined views
    const setView = (viewName) => {
      if (!productMesh) return;
      
      switch(viewName) {
        case 'front':
          gsapPositionCamera(0, 0, 5);
          break;
        case 'back':
          gsapPositionCamera(0, 0, -5);
          break;
        case 'left':
          gsapPositionCamera(-5, 0, 0);
          break;
        case 'right':
          gsapPositionCamera(5, 0, 0);
          break;
        case 'top':
          gsapPositionCamera(0, 5, 0);
          break;
        default:
          gsapPositionCamera(0, 0, 5);
      }
    };
    
    // Simple function to animate camera position
    const gsapPositionCamera = (x, y, z) => {
      // For this simplified version, we'll just set the position
      // In a real app, use GSAP for smooth animations
      camera.position.set(x, y, z);
      camera.lookAt(0, 0, 0);
      controls.update();
    };
    
    // Set initial view
    setView(currentView);
    
    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      
      if (controlsRef.current) {
        controlsRef.current.update();
      }
      
      if (productMesh && !isLoading) {
        productMesh.rotation.y += 0.002; // Gentle auto-rotation
      }
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Handle resize
    const handleResize = () => {
      if (mountRef.current) {
        const width = mountRef.current.clientWidth;
        const height = mountRef.current.clientHeight;
        
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    // Initial resize
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [productName, initialColor, currentView]);
  
  // Handle view changes
  const changeView = (view) => {
    setCurrentView(view);
  };
  
  return (
    <div className="w-full flex flex-col">
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-70">
            <div className="text-blue-500">Loading 3D Model...</div>
          </div>
        )}
        
        <div 
          ref={mountRef} 
          className="w-full h-64 border border-gray-200 rounded-lg shadow-sm"
          style={{ aspectRatio: '1/1', maxWidth: '400px', margin: '0 auto' }}
        />
      </div>
      
      <div className="mt-4 flex flex-wrap justify-center space-x-2">
  <button
    onClick={() => changeView('front')}
    className={`px-3 py-1 text-sm rounded w-auto ${currentView === 'front' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
  >
    Front
  </button>
  <button
    onClick={() => changeView('back')}
    className={`px-3 py-1 mx-2 text-sm rounded w-auto ${currentView === 'back' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
  >
    Back
  </button>
  <button
    onClick={() => changeView('left')}
    className={`px-3 py-1 mx-2 text-sm rounded w-auto ${currentView === 'left' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
  >
    Left
  </button>
  <button
    onClick={() => changeView('right')}
    className={`px-3 py-1 mx-2 text-sm rounded w-auto ${currentView === 'right' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
  >
    Top
  </button>
</div>

      
      <div className="mt-6">
        <h2 className="text-xl font-bold">{productName}</h2>
        <p className="mt-2 text-gray-600">{productDescription}</p>
      </div>
    </div>
  );
};

export default Product3DViewer;